import { useEffect } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import HeroSection from './components/HeroSection';
import CategoryGrid from './components/CategoryGrid';
import FeaturedArticles from './components/FeaturedArticles';
import TimelineSection from './components/TimelineSection';
import CTASection from './components/CTASection';
import useSEOMeta from '@/hooks/useSEOMeta';
import { getRecentArticles } from '@/mocks/articles';

export default function Home() {
  const recentArticles = getRecentArticles().slice(0, 4);

  useSEOMeta({
    title: '苍井空——从成人偶像到艺术家的真实人生 | 苍井空人物志',
    description: '深度解析苍井空的人生轨迹、职业转型、婚姻生活及在华影响力。从成人偶像到艺术家，还原一个真实立体的苍井空。涵盖最新动态、艺术生涯等全方位内容。',
    keywords: '苍井空,苍井空个人资料,苍井空转型经历,苍井空婚姻生活,苍井空在中国,苍井空最新动态,苍井空艺术生涯',
    canonicalUrl: 'https://cangjingkong.cn/',
    ogImage: 'https://readdy.ai/api/search-image?query=elegant%20Japanese%20art%20studio%20with%20warm%20golden%20sunlight%2C%20calligraphy%20brushes%2C%20serene%20atmosphere%2C%20cream%20and%20beige%20tones&width=1200&height=630&seq=sora-og-home&orientation=landscape',
  });

  const websiteSchema = {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: '苍井空人物志',
    url: 'https://cangjingkong.cn/',
    description: '深度解析苍井空的人生轨迹、职业转型、婚姻生活及在华影响力。还原一个真实立体的苍井空。',
    potentialAction: {
      '@type': 'SearchAction',
      target: 'https://cangjingkong.cn/search?q={search_term_string}',
      'query-input': 'required name=search_term_string',
    },
  };

  const orgSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: '苍井空人物志',
    url: 'https://cangjingkong.cn/',
    description: '专注于深度解析苍井空人生故事的独立内容平台',
  };

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(websiteSchema)}</script>
      <script type="application/ld+json">{JSON.stringify(orgSchema)}</script>
      <script type="application/ld+json">{JSON.stringify({
        '@context': 'https://schema.org',
        '@type': 'WebPage',
        name: '苍井空——从成人偶像到艺术家的真实人生',
        description: '深度解析苍井空的人生轨迹、职业转型、婚姻生活及在华影响力。',
        url: 'https://cangjingkong.cn/',
        isPartOf: { '@type': 'WebSite', name: '苍井空人物志', url: 'https://cangjingkong.cn/' },
      })}</script>

      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main>
          <HeroSection />
          <CategoryGrid />
          <FeaturedArticles />

          <section className="py-14 md:py-20 bg-background-50">
            <div className="w-full px-4 md:px-6 max-w-7xl mx-auto">
              <div className="text-center mb-10 md:mb-14">
                <h2 className="text-2xl md:text-3xl font-semibold text-foreground-950" style={{ fontFamily: 'var(--font-heading)' }}>
                  最新发布
                </h2>
                <p className="mt-3 text-foreground-600 max-w-xl mx-auto">
                  持续更新苍井空的最新动态与深度文章
                </p>
                <div className="mt-4 w-12 h-0.5 bg-primary-500 mx-auto"></div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
                {recentArticles.map((article) => (
                  <a
                    key={article.id}
                    href={`/article/${article.slug}`}
                    onClick={(e) => {
                      e.preventDefault();
                      window.scrollTo({ top: 0, behavior: 'smooth' });
                      setTimeout(() => {
                        window.location.href = `/article/${article.slug}`;
                      }, 300);
                    }}
                    className="group flex flex-col rounded-lg overflow-hidden bg-background-50 border border-background-200/70 hover:border-primary-300/50 transition-colors cursor-pointer"
                  >
                    <div className="aspect-[4/3] overflow-hidden">
                      <img
                        src={article.coverImage}
                        alt={article.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        loading="lazy"
                      />
                    </div>
                    <div className="p-4 flex-1 flex flex-col">
                      <span className="text-xs text-foreground-500">{article.publishDate}</span>
                      <h4 className="mt-2 text-sm font-semibold text-foreground-950 group-hover:text-primary-500 transition-colors line-clamp-2 flex-1">
                        {article.title}
                      </h4>
                    </div>
                  </a>
                ))}
              </div>
            </div>
          </section>

          <TimelineSection />
          <CTASection />
        </main>
        <Footer />
      </div>
    </>
  );
}